import packageJson from "../../../../../package.json";
import {
  EUserType,
  IChatOptions,
  IConfiguracoesEducador,
  IEscola,
  IMensagemCategoria,
  IOpcoesConfiguracoesEducador,
  IPeriodoEtapa,
  ITreeViewTurma,
} from "@deltasge/marauders-map";

import { takeLatest, select, put, call } from "redux-saga/effects";

import { snack } from "components/GlobalSnackbar";
import { api, setToken, setBaseUrl } from "configs/axios";
import { history } from "configs/history";
import { RootState } from "store/modules/root_reducer";
import { resetStore } from "store/modules/app";

import { request as setAnoLetivo } from "store/modules/ano-letivo";
import { ActionTypes } from "./types";
import { failure, success } from "./actions";
import { set as setEscola } from "store/modules/escola";
import {
  ConfiguracoesEducador,
  set as setConfiguracoes,
} from "store/modules/configuracoes";
import { set as setMensagemCategoria } from "store/modules/categorias";
import { IVersaoApp, set as setVersao } from "store/modules/versao";
import { set as setTreeView } from "store/modules/tree-view";
import { Environment, cacheDados } from "configs";
import { setUserId, setUserProperties } from "firebase/analytics";
import { analytics } from "App";
import {
  getKeyChatOptions,
  getKeyConfig,
  getKeyEscola,
  getKeyEtapas,
  getKeyMensagemCategoria,
  getKeyTreeview,
  getKeyVersao,
} from "utils";
import { setChatOptions } from "store/modules/chat";
import { AxiosResponse } from "axios";

function* request() {
  try {
    const { Authorization = "" } = api.defaults.headers.common;
    const {
      usuario: { usuario },
    }: RootState = yield select();

    if (!Authorization) {
      if (!usuario || !usuario.token) {
        yield put(failure());
        history.push("/login");
        return;
      }
      const codigoZeroEsqueda = usuario.escolaId.toString().padStart(4, "0");
      setToken(usuario.token);
      setBaseUrl(codigoZeroEsqueda);
      setUserId(analytics, usuario.deltaId);
      setUserProperties(analytics, { escola: codigoZeroEsqueda });
      setUserProperties(analytics, { tipoUsuario: usuario.tipoUsuario });
      setUserProperties(analytics, { aplicativos: usuario.aplicativos });
    }

    if (!usuario) {
      throw new Error("Usuario não encontrado");
    }

    let anoSite = new Date().getFullYear();
    const cache24h = 60 * 24;

    const keyEscola = getKeyEscola(usuario);
    const cacheEscola = cacheDados.get<IEscola>(keyEscola);
    if (
      cacheEscola &&
      typeof cacheEscola != "string" &&
      Object.keys(cacheEscola).length > 0
    ) {
      anoSite = parseInt((cacheEscola as IEscola).anoSite, 10);
      yield put(setEscola(cacheEscola as IEscola));
    } else {
      const { data } = yield call(api.get, `/escola/`);
      if (data && data[0]) {
        anoSite = parseInt((data[0][0] as IEscola).anoSite, 10);
        yield put(setEscola(data[0][0] as IEscola));
        cacheDados.set(keyEscola, data[0][0] as IEscola, cache24h);
      }
    }

    const keyConfig = getKeyConfig(usuario);
    const cacheConfig = cacheDados.get<ConfiguracoesEducador>(keyConfig);
    if (
      cacheConfig &&
      typeof cacheConfig != "string" &&
      Object.keys(cacheConfig).length > 0
    ) {
      yield put(setConfiguracoes(cacheConfig));
    } else {
      const opcoes: Partial<IOpcoesConfiguracoesEducador> = {
        tiposAvaliacoes: true,
        configOnline: true,
      };

      const { data: configuracoes } = yield call(
        api.get,
        `/educador/configuracoes/${anoSite}`,
        {
          clearCacheEntry: true,
          params: { opcoes },
          baseURL: `${Environment.API_URL}/api/v2/${usuario?.escolaId}`,
        }
      );
      if (configuracoes) {
        const { configOnline, tiposAvaliacoes, etapas } =
          configuracoes as Partial<IConfiguracoesEducador>;
        if (!tiposAvaliacoes || !configOnline) {
          throw new Error(
            "Etapas, Tipos de avaliações e Configurações do Online são obrigatórios "
          );
        }
        yield put(setConfiguracoes({ configOnline, etapas, tiposAvaliacoes }));
        cacheDados.set(
          keyConfig,
          configuracoes as Partial<IConfiguracoesEducador>,
          cache24h
        );
      }
    }

    const keyVersao = getKeyVersao(usuario);
    const cacheVersao = cacheDados.get<IVersaoApp>(keyVersao);
    if (
      cacheVersao &&
      typeof cacheVersao != "string" &&
      Object.keys(cacheVersao).length > 0
    ) {
      yield put(setVersao(cacheVersao));
    } else {
      const { data: versao } = yield call(api.get, `/versao/`);
      if (versao) {
        versao.app = packageJson.version;
        yield put(setVersao(versao));
        cacheDados.set(keyVersao, versao as IVersaoApp, cache24h);
      }
    }

    const keyMensagemCategoria = getKeyMensagemCategoria(usuario);
    const cacheMensagemCategoria =
      cacheDados.get<IMensagemCategoria[]>(keyMensagemCategoria);
    if (
      cacheMensagemCategoria &&
      typeof cacheMensagemCategoria != "string" &&
      Object.keys(cacheMensagemCategoria).length > 0
    ) {
      yield put(setMensagemCategoria(cacheMensagemCategoria));
    } else {
      const { data } = yield call(api.get, "educador/mensagem-categoria");
      if (!data || !Array.isArray(data) || !Array.isArray(data.at(0))) {
        throw new Error("Nenhuma categoria de mensagem encontrada");
      }
      yield put(setMensagemCategoria(data[0] as IMensagemCategoria[]));
      cacheDados.set(
        keyMensagemCategoria,
        data[0] as IMensagemCategoria[],
        cache24h
      );
    }

    const keyEtapas = getKeyEtapas(usuario, anoSite.toString());
    const cacheEtapas = cacheDados.get<IPeriodoEtapa[]>(keyEtapas);
    if (
      cacheEtapas &&
      typeof cacheEtapas != "string" &&
      Array.isArray(cacheEtapas)
    ) {
      yield put(setConfiguracoes({ etapas: cacheEtapas }));
    } else {
      const opcoes: Partial<IOpcoesConfiguracoesEducador> = { etapas: true };

      const { data: configuracoes } = yield call(
        api.get,
        `/educador/configuracoes/${anoSite}`,
        {
          clearCacheEntry: true,
          params: { opcoes },
          baseURL: `${Environment.API_URL}/api/v2/${usuario?.escolaId}`,
        }
      );
      if (configuracoes) {
        const { etapas } = configuracoes as Partial<IConfiguracoesEducador>;
        if (!etapas) {
          throw new Error("Etapas não encontradas");
        }
        yield put(setConfiguracoes({ etapas }));
        cacheDados.set(keyEtapas, etapas, cache24h);
      }
    }

    const keyTreeview = getKeyTreeview(usuario, anoSite.toString());
    const cacheTreeview = cacheDados.get<ITreeViewTurma>(keyTreeview);
    if (
      cacheTreeview &&
      typeof cacheTreeview != "string" &&
      Object.keys(cacheTreeview).length > 0
    ) {
      yield put(setTreeView(cacheTreeview));
    } else {
      const { data: turmas } = yield call(
        api.get,
        `/turma/tree-view/${anoSite}`.concat(
          usuario?.tipoUsuario == EUserType.Professor
            ? `/professor/${usuario.id}`
            : ""
        )
      );
      if (turmas) {
        yield put(setTreeView(turmas as ITreeViewTurma));
        cacheDados.set(keyTreeview, turmas as ITreeViewTurma, cache24h);
      }
    }

    const keyChatOptions = getKeyChatOptions(usuario);
    const cacheChatOptions = cacheDados.get<IChatOptions>(keyChatOptions);
    if (
      cacheChatOptions &&
      typeof cacheChatOptions != "string" &&
      Object.keys(cacheChatOptions).length > 0
    ) {
      yield put(setChatOptions(cacheChatOptions));
    } else {
      const { data: chatOptions }: AxiosResponse<IChatOptions | undefined> =
        yield call(api.get, `/conversa/options/`);
      if (chatOptions) {
        yield put(setChatOptions(chatOptions));
        cacheDados.set(keyChatOptions, chatOptions, cache24h);
      }
    }

    yield put(setAnoLetivo(anoSite));
    yield put(success());
  } catch (error) {
    yield put(failure());
    yield put(resetStore());

    snack.warning((error as Error).toString());
    history.push("/login");
  }
}

export const sagas = takeLatest([ActionTypes.REQUEST], request);
